import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Copy,
  Eye,
  FileDown,
  FileText,
  Info,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { toast } from "react-toastify";
import PageHeader from "../../../components/PageHeader";
import Modal from "../../../components/Modal";
import UndMedidaSelect from "../../../components/ControleProducao/UndMedidaSelect";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { BrazilDate } from "../../../utils/BrazilDate";
import {
  DIAS_SEMANA_BASE,
  isoWeekdayFromISO,
  labelDiaSemanaCurto,
} from "../../../utils/controleProducaoDates";
import { getErrorMessage } from "../../../utils/errorHandler";
import {
  FichaTransferencia,
  FichaTransferenciaItem,
  FichaTransferenciaLista,
  ProdutoFichaTransferencia,
  fichaTransferenciaService,
} from "../../../services/fichaTransferenciaService";
import { baixarPdfFichaTransferencia } from "./fichaTransferenciaPdf";
import {
  textoConsolidadoFichaTransferencia,
  type PdfFichaTransferenciaInput,
} from "./fichaTransferenciaPdfDados";
import {
  erroListasForm,
  ListaForm,
  listaFormDaApi,
  ListasFornecedoresSecao,
  listasFormParaPayload,
} from "./ListasFornecedores";

type Modo = "lista" | "form";

type Formulario = {
  id: number | null;
  dataReferencia: string;
  previsaoEntregaDiaSemana: number | "";
  diaProgramado: boolean;
  producaoAssinada: boolean;
  origem: string;
  destino: string;
  criadoPor: string | null;
  itens: FichaTransferenciaItem[];
  listas: ListaForm[];
};

function entradaConsolidada(ficha: {
  origem: string;
  destino: string;
  dataReferencia: string;
  previsaoEntrega: string;
  itens: FichaTransferenciaItem[];
  listas: PdfFichaTransferenciaInput["listas"];
}): PdfFichaTransferenciaInput {
  return {
    origem: ficha.origem,
    destino: ficha.destino,
    dataReferencia: ficha.dataReferencia,
    previsaoEntrega: ficha.previsaoEntrega,
    itens: ficha.itens.map((item) => ({
      descricao: item.descricao,
      quantidadeTransferencia: item.quantidadeTransferencia,
      unidade: item.unidade,
    })),
    listas: ficha.listas,
  };
}

type ProdutoOpt = { value: number; label: string };

const PRODUTO_SELECT_STYLES = {
  ...(SELECT_STYLES as object),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 10050,
  }),
} as unknown as StylesConfig<ProdutoOpt, false>;

function dataBR(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}

function horaBR(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "";
  return data.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

function chaveDescricao(descricao: string): string {
  return descricao.trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

function somarQuantidade(atual: string, adicionada: string): string {
  const a = Number(atual.trim().replace(",", ".")) || 0;
  const b = Number(adicionada.trim().replace(",", ".")) || 0;
  const soma = Math.round((a + b) * 1000) / 1000;
  return Number.isInteger(soma) ? String(soma) : String(soma);
}

function quantidadeValida(valor: string): boolean {
  const texto = valor.trim().replace(",", ".");
  return /^\d+(\.\d+)?$/.test(texto);
}

function textoReferencia(valor: string | null): string {
  const texto = (valor ?? "").trim();
  return texto || "—";
}

const TOM_DIA: Record<number, string> = {
  1: "bg-sky-100 text-sky-900",
  2: "bg-emerald-100 text-emerald-900",
  3: "bg-amber-100 text-amber-950",
  4: "bg-violet-100 text-violet-900",
  5: "bg-rose-100 text-rose-900",
  6: "bg-orange-100 text-orange-900",
  7: "bg-neutral-200 text-neutral-800",
};

const ControleProducaoFichaTransferenciaPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [modo, setModo] = useState<Modo>("lista");
  const [lista, setLista] = useState<FichaTransferenciaLista | null>(null);
  const [filtroData, setFiltroData] = useState("");
  const [page, setPage] = useState(1);
  const [loadingLista, setLoadingLista] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState<Formulario | null>(null);
  const [sujo, setSujo] = useState(false);
  const [produtos, setProdutos] = useState<ProdutoFichaTransferencia[]>([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [produtoSelecionado, setProdutoSelecionado] = useState<number | null>(
    null
  );
  const [quantidadeNova, setQuantidadeNova] = useState("");
  const [undMedidaNova, setUndMedidaNova] = useState<number | "">("");
  const [unds, setUnds] = useState<{ id: number; nome: string }[]>([]);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [ajudaPrevisto, setAjudaPrevisto] = useState(false);
  const [textoConsolidado, setTextoConsolidado] = useState<string | null>(null);

  const dataQuery = searchParams.get("data") ?? "";
  const dataCarregada = React.useRef("");

  const carregarLista = useCallback(async (pagina: number, data: string) => {
    setLoadingLista(true);
    try {
      const resposta = await fichaTransferenciaService.listar({
        page: pagina,
        pageSize: 10,
        data: data || undefined,
      });
      setLista(resposta);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingLista(false);
    }
  }, []);

  const garantirProdutos = useCallback(async () => {
    if (produtos.length > 0) return produtos;
    const listaProdutos = await fichaTransferenciaService.produtos();
    setProdutos(listaProdutos);
    return listaProdutos;
  }, [produtos]);

  const abrirFicha = useCallback((ficha: FichaTransferencia) => {
    setForm({
      id: ficha.id,
      dataReferencia: ficha.dataReferencia,
      previsaoEntregaDiaSemana: ficha.previsaoEntregaDiaSemana,
      diaProgramado: ficha.diaProgramado,
      producaoAssinada: ficha.producaoAssinada,
      origem: ficha.origem.rotulo,
      destino: ficha.destino.rotulo,
      criadoPor: ficha.criadoPor.nome,
      itens: ficha.itens,
      listas: (ficha.listas ?? []).map(listaFormDaApi),
    });
    setUnds(ficha.unds);
    setSujo(false);
    setModo("form");
  }, []);

  const abrirData = useCallback(
    async (data: string) => {
      setLoadingForm(true);
      setSomenteLeitura(false);
      setModo("form");
      try {
        await garantirProdutos();
        const sugestao = await fichaTransferenciaService.sugestao(data);
        setForm({
          id: null,
          dataReferencia: sugestao.dataReferencia,
          previsaoEntregaDiaSemana: "",
          diaProgramado: sugestao.diaProgramado,
          producaoAssinada: sugestao.producaoAssinada,
          origem: sugestao.origem.rotulo,
          destino: sugestao.destino.rotulo,
          criadoPor: null,
          itens: sugestao.itens,
          listas: [],
        });
        setUnds(sugestao.unds);
        setSujo(false);
      } catch (error) {
        dataCarregada.current = "";
        toast.error(getErrorMessage(error));
        setSearchParams({});
        setModo("lista");
      } finally {
        setLoadingForm(false);
      }
    },
    [garantirProdutos, setSearchParams]
  );

  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dataQuery)) {
      dataCarregada.current = "";
      return;
    }
    if (dataCarregada.current === dataQuery) return;
    dataCarregada.current = dataQuery;
    void abrirData(dataQuery);
  }, [dataQuery, abrirData]);

  useEffect(() => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dataQuery)) return;
    void carregarLista(page, filtroData);
  }, [dataQuery, page, filtroData, carregarLista]);

  const produtoOptions = useMemo<ProdutoOpt[]>(
    () =>
      produtos.map((produto) => ({
        value: produto.id,
        label: `${produto.descricao} · ${produto.lojaNome}`,
      })),
    [produtos]
  );

  function atualizarUnidade(produtoId: number, undMedidaId: number | "") {
    setForm((atual) =>
      atual
        ? {
            ...atual,
            itens: atual.itens.map((item) =>
              item.produtoId === produtoId
                ? {
                    ...item,
                    undMedidaId: undMedidaId === "" ? null : undMedidaId,
                    unidade:
                      unds.find((und) => und.id === undMedidaId)?.nome ??
                      item.unidade,
                  }
                : item
            ),
          }
        : atual
    );
    setSujo(true);
  }

  function atualizarItem(produtoId: number, quantidadeTransferencia: string) {
    setForm((atual) =>
      atual
        ? {
            ...atual,
            itens: atual.itens.map((item) =>
              item.produtoId === produtoId
                ? { ...item, quantidadeTransferencia }
                : item
            ),
          }
        : atual
    );
    setSujo(true);
  }

  function removerItem(produtoId: number) {
    setForm((atual) =>
      atual
        ? {
            ...atual,
            itens: atual.itens.filter((item) => item.produtoId !== produtoId),
          }
        : atual
    );
    setSujo(true);
  }

  function confirmarItemManual() {
    if (!form || produtoSelecionado == null) {
      toast.warning("Selecione um produto.");
      return;
    }
    if (!quantidadeValida(quantidadeNova)) {
      toast.warning("Informe uma quantidade válida para transferência.");
      return;
    }
    if (undMedidaNova === "") {
      toast.warning("Selecione a unidade de medida.");
      return;
    }
    const produto = produtos.find((item) => item.id === produtoSelecionado);
    if (!produto) {
      toast.error("Produto não encontrado.");
      return;
    }
    const chave = chaveDescricao(produto.descricao);
    const existente = form.itens.find(
      (item) =>
        item.produtoId === produto.id ||
        chaveDescricao(item.descricao) === chave
    );
    if (existente) {
      atualizarItem(
        existente.produtoId,
        somarQuantidade(existente.quantidadeTransferencia, quantidadeNova)
      );
      toast.info("O produto já estava na ficha. A quantidade foi somada.");
    } else {
      setForm({
        ...form,
        itens: [
          ...form.itens,
          {
            produtoId: produto.id,
            descricao: produto.descricao,
            unidade:
              unds.find((und) => und.id === undMedidaNova)?.nome ||
              produto.unidade,
            undMedidaId: undMedidaNova,
            quantidadePrevista: null,
            quantidadeSobraAnterior: null,
            quantidadeProduzida: null,
            quantidadeTransferencia: quantidadeNova.trim().replace(",", "."),
            itemManual: true,
          },
        ],
      });
      setSujo(true);
    }
    setModalAberto(false);
    setProdutoSelecionado(null);
    setQuantidadeNova("");
    setUndMedidaNova("");
  }

  async function salvar() {
    if (!form) return;
    if (form.previsaoEntregaDiaSemana === "") {
      toast.warning("Selecione a previsão de entrega.");
      return;
    }
    if (form.itens.length === 0) {
      toast.warning("Inclua ao menos um item.");
      return;
    }
    if (form.itens.some((item) => item.undMedidaId == null)) {
      toast.warning("Selecione a unidade de medida de cada item.");
      return;
    }
    if (
      form.itens.some((item) => !quantidadeValida(item.quantidadeTransferencia))
    ) {
      toast.warning("A quantidade para transferência não pode ser negativa.");
      return;
    }
    const erroListas = erroListasForm(form.listas, form.dataReferencia);
    if (erroListas) {
      toast.warning(erroListas);
      return;
    }
    setSalvando(true);
    try {
      const itens = form.itens.map((item) => ({
        produtoId: item.produtoId,
        quantidadeTransferencia: item.quantidadeTransferencia
          .trim()
          .replace(",", "."),
        undMedidaId: item.undMedidaId as number,
      }));
      const listas = listasFormParaPayload(form.listas);
      const salva = form.id
        ? await fichaTransferenciaService.atualizar(form.id, {
            previsaoEntregaDiaSemana: form.previsaoEntregaDiaSemana,
            itens,
            listas,
          })
        : await fichaTransferenciaService.criar({
            dataReferencia: form.dataReferencia,
            previsaoEntregaDiaSemana: form.previsaoEntregaDiaSemana,
            itens,
            listas,
          });
      abrirFicha(salva);
      toast.success("Ficha de transferência salva.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  }

  function entradaDoFormulario(atual: Formulario): PdfFichaTransferenciaInput {
    return entradaConsolidada({
      origem: atual.origem,
      destino: atual.destino,
      dataReferencia: atual.dataReferencia,
      previsaoEntrega:
        DIAS_SEMANA_BASE.find(
          (dia) => dia.valor === atual.previsaoEntregaDiaSemana
        )?.label ?? "",
      itens: atual.itens,
      listas: listasFormParaPayload(atual.listas),
    });
  }

  async function gerarPdfAtual() {
    if (!form?.id || sujo) {
      toast.warning("Salve a ficha antes de gerar o PDF.");
      return;
    }
    await baixarPdfFichaTransferencia(entradaDoFormulario(form));
  }

  async function gerarPdfDaLista(id: number) {
    try {
      const ficha = await fichaTransferenciaService.obter(id);
      await baixarPdfFichaTransferencia(
        entradaConsolidada({
          origem: ficha.origem.rotulo,
          destino: ficha.destino.rotulo,
          dataReferencia: ficha.dataReferencia,
          previsaoEntrega: ficha.previsaoEntrega,
          itens: ficha.itens,
          listas: ficha.listas ?? [],
        })
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  async function copiarTextoConsolidado() {
    if (!textoConsolidado) return;
    try {
      await navigator.clipboard.writeText(textoConsolidado);
      toast.success("Texto copiado. É só colar no WhatsApp.");
    } catch {
      toast.warning(
        "Não foi possível copiar. Selecione o texto e copie manualmente."
      );
    }
  }

  async function excluirFicha(id: number) {
    if (
      !window.confirm(
        "Excluir esta ficha de transferência? Não dá para desfazer."
      )
    ) {
      return;
    }
    try {
      await fichaTransferenciaService.excluir(id);
      toast.success("Ficha de transferência excluída.");
      if (form?.id === id) {
        setForm(null);
        setModo("lista");
        setSearchParams({});
      }
      await carregarLista(page, filtroData);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  async function atualizarProducao() {
    if (!form?.id) return;
    if (sujo) {
      toast.warning("Salve a ficha antes de atualizar a produção.");
      return;
    }
    setSalvando(true);
    try {
      const ficha = await fichaTransferenciaService.atualizarProducao(form.id);
      abrirFicha(ficha);
      toast.success(
        "Produção, base e sobra do dia anterior foram atualizadas."
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  }

  async function abrirPorId(id: number, leitura: boolean) {
    setSomenteLeitura(leitura);
    setLoadingForm(true);
    setModo("form");
    try {
      await garantirProdutos();
      const ficha = await fichaTransferenciaService.obter(id);
      abrirFicha(ficha);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setModo("lista");
    } finally {
      setLoadingForm(false);
    }
  }

  const totalPaginas = lista
    ? Math.max(1, Math.ceil(lista.total / lista.pageSize))
    : 1;

  return (
    <div className="mx-auto w-full max-w-7xl px-3 pb-10 sm:px-4">
      {modo === "lista" ? (
        <>
          <PageHeader
            title="Ficha de Transferência"
            description="Produtos produzidos em Birigui que serão enviados para Araçatuba — Loja 2."
            actions={
              <button
                type="button"
                onClick={() => {
                  setFiltroData("");
                  setPage(1);
                  setSearchParams({ data: BrazilDate.todayISO() });
                }}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
              >
                Nova transferência
              </button>
            }
          />

          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="text-sm font-medium text-neutral-700">
              Ficha diária
              <input
                type="date"
                value={filtroData}
                onChange={(event) => {
                  setPage(1);
                  setFiltroData(event.target.value);
                }}
                className="mt-1 block h-10 rounded-lg border border-neutral-300 px-3 text-sm"
              />
            </label>
            {filtroData ? (
              <button
                type="button"
                onClick={() => {
                  setPage(1);
                  setFiltroData("");
                }}
                className="h-10 rounded-md border border-neutral-300 px-3 text-sm font-semibold text-neutral-700"
              >
                Limpar
              </button>
            ) : null}
          </div>

          {loadingLista ? (
            <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium text-neutral-600">
                Carregando fichas…
              </p>
            </div>
          ) : !lista || lista.itens.length === 0 ? (
            <p className="py-10 text-center text-sm text-neutral-500">
              Nenhuma ficha de transferência encontrada.
            </p>
          ) : (
            <>
              <div className="space-y-3 md:hidden">
                {lista.itens.map((item) => {
                  const diaFicha = isoWeekdayFromISO(item.dataReferencia);
                  return (
                    <article
                      key={item.id}
                      className="rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <BadgeDia
                              dia={isoWeekdayFromISO(item.dataReferencia)}
                              rotulo={labelDiaSemanaCurto(diaFicha)}
                            />
                            <h3 className="text-base font-semibold text-neutral-900">
                              {dataBR(item.dataReferencia)}
                            </h3>
                            <span className="text-xs font-medium text-neutral-500">
                              {horaBR(item.createdAt)}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-neutral-600">
                            {item.rotuloOrigem} → {item.rotuloDestino}
                          </p>
                        </div>
                        <AcoesLista
                          onVer={() => void abrirPorId(item.id, true)}
                          onEditar={() => void abrirPorId(item.id, false)}
                          onPdf={() => void gerarPdfDaLista(item.id)}
                          onExcluir={() => void excluirFicha(item.id)}
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <BadgeDia
                          dia={item.previsaoEntregaDiaSemana}
                          rotulo={`Entrega ${labelDiaSemanaCurto(item.previsaoEntregaDiaSemana)}`}
                        />
                        <span className="inline-flex items-center rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
                          {item.quantidadeItens}{" "}
                          {item.quantidadeItens === 1 ? "item" : "itens"}
                        </span>
                        <BadgeListas quantidade={item.quantidadeListas} />
                      </div>
                      <p className="mt-2 text-xs text-neutral-500">
                        Responsável: {item.criadoPor.nome}
                      </p>
                    </article>
                  );
                })}
              </div>
              <div className="hidden overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm md:block">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                      <th className="px-4 py-3">Ficha diária</th>
                      <th className="px-4 py-3">Rota</th>
                      <th className="px-4 py-3">Entrega</th>
                      <th className="px-4 py-3">Itens</th>
                      <th className="px-4 py-3">Responsável</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.itens.map((item) => {
                      const diaFicha = isoWeekdayFromISO(item.dataReferencia);
                      return (
                        <tr
                          key={item.id}
                          className="border-b border-neutral-100 last:border-0"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <BadgeDia
                                dia={diaFicha}
                                rotulo={labelDiaSemanaCurto(diaFicha)}
                              />
                              <span className="font-semibold text-neutral-900">
                                {dataBR(item.dataReferencia)}
                              </span>
                              <span className="text-xs font-medium text-neutral-500">
                                {horaBR(item.createdAt)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-neutral-700">
                            {item.rotuloOrigem} → {item.rotuloDestino}
                          </td>
                          <td className="px-4 py-3">
                            <BadgeDia
                              dia={item.previsaoEntregaDiaSemana}
                              rotulo={item.previsaoEntrega}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="inline-flex rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-700">
                                {item.quantidadeItens}
                              </span>
                              <BadgeListas quantidade={item.quantidadeListas} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-neutral-700">
                            {item.criadoPor.nome}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end">
                              <AcoesLista
                                onVer={() => void abrirPorId(item.id, true)}
                                onEditar={() => void abrirPorId(item.id, false)}
                                onPdf={() => void gerarPdfDaLista(item.id)}
                                onExcluir={() => void excluirFicha(item.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-neutral-500">
                  {lista.total} {lista.total === 1 ? "ficha" : "fichas"} · 10
                  por página
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((atual) => Math.max(1, atual - 1))}
                    className="rounded-md border border-neutral-300 px-3 py-1.5 font-semibold disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-neutral-600">
                    {page} / {totalPaginas}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPaginas}
                    onClick={() => setPage((atual) => atual + 1)}
                    className="rounded-md border border-neutral-300 px-3 py-1.5 font-semibold disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <PageHeader
            title="Ficha de Transferência"
            description={
              somenteLeitura
                ? "Você está só olhando esta ficha. Para mudar alguma coisa, toque em Editar."
                : "A quantidade transferida não altera a produção diária."
            }
            actions={
              <div className="flex flex-wrap gap-2">
                {somenteLeitura ? (
                  <button
                    type="button"
                    onClick={() => setSomenteLeitura(false)}
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
                  >
                    <Pencil className="h-4 w-4" />
                    Editar
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setSearchParams({});
                    setForm(null);
                    setSomenteLeitura(false);
                    setModo("lista");
                  }}
                  className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700"
                >
                  Voltar
                </button>
              </div>
            }
          />

          {loadingForm || !form ? (
            <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium text-neutral-600">
                Montando a ficha…
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 grid gap-3 rounded-xl border border-neutral-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
                <CampoLeitura label="Origem" valor={form.origem} />
                <CampoLeitura label="Destino" valor={form.destino} />
                <label className="text-sm font-medium text-neutral-700">
                  Ficha diária
                  <input
                    type="date"
                    value={form.dataReferencia}
                    disabled={form.id != null}
                    onChange={(event) => {
                      const data = event.target.value;
                      if (!data) return;
                      setSearchParams({ data });
                    }}
                    className="mt-1 block h-10 w-full rounded-lg border border-neutral-300 px-3 text-sm disabled:bg-neutral-100"
                  />
                </label>
                <label className="text-sm font-medium text-neutral-700">
                  Previsão de entrega
                  <select
                    value={form.previsaoEntregaDiaSemana}
                    disabled={somenteLeitura}
                    onChange={(event) => {
                      const valor = event.target.value;
                      setForm({
                        ...form,
                        previsaoEntregaDiaSemana:
                          valor === "" ? "" : Number(valor),
                      });
                      setSujo(true);
                    }}
                    className="mt-1 block h-10 w-full rounded-lg border border-neutral-300 px-3 text-sm disabled:bg-neutral-100"
                  >
                    <option value="">Selecione o dia</option>
                    {DIAS_SEMANA_BASE.map((dia) => (
                      <option key={dia.valor} value={dia.valor}>
                        {dia.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <section className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-3 sm:p-4">
                <h2 className="mb-1 text-base font-semibold text-neutral-900">
                  Ficha diária de produção
                </h2>
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <p className="text-sm text-neutral-600">
                    Enquanto a produção da ficha diária não for finalizada, a
                    transferência já vem com a quantidade cheia da sugestão
                    (base menos a sobra). Depois que a produção for finalizada,
                    use Atualizar produção para trocar pela quantidade
                    produzida.
                    {!form.diaProgramado
                      ? " Este não é um dia habitual de produção em Birigui."
                      : ""}
                    {form.criadoPor ? ` Responsável: ${form.criadoPor}.` : ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => setAjudaPrevisto(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-sm font-semibold text-primary"
                  >
                    <Info className="h-4 w-4" />
                    De onde vem cada número?
                  </button>
                </div>

                {form.itens.some(
                  (item) =>
                    item.producaoFinalizada === false && !item.itemManual
                ) ? (
                  <p className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-950">
                    Ainda há itens sem produção finalizada na ficha diária.
                    Nesses itens, a transferência já está com a quantidade cheia
                    da sugestão.
                  </p>
                ) : null}

                {somenteLeitura ? null : (
                  <div className="mb-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setModalAberto(true)}
                      className="inline-flex items-center gap-1 rounded-md border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-800"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar item
                    </button>
                  </div>
                )}

                {form.itens.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-neutral-300 py-10 text-center text-sm text-neutral-500">
                    Nenhum item nesta ficha. Adicione os produtos que serão
                    enviados.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                          <th className="px-3 py-2">Produto</th>
                          <th className="px-3 py-2">Unidade</th>
                          <th className="px-3 py-2 text-right">
                            <span className="inline-flex items-center justify-end gap-1">
                              Base
                              <button
                                type="button"
                                onClick={() => setAjudaPrevisto(true)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-primary hover:bg-primary/10"
                                aria-label="De onde vem cada número"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </span>
                          </th>
                          <th className="px-3 py-2 text-right">
                            Sobra dia anterior
                          </th>
                          <th className="px-3 py-2 text-right">Produzido</th>
                          <th className="px-3 py-2 text-right">
                            Transferência
                          </th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {form.itens.map((item) => (
                          <tr
                            key={item.produtoId}
                            className={`border-b border-neutral-100 ${
                              item.producaoFinalizada === false &&
                              !item.itemManual
                                ? "bg-amber-50"
                                : ""
                            }`}
                          >
                            <td className="px-3 py-2 font-medium text-neutral-900">
                              {item.descricao}
                              {item.producaoFinalizada === false &&
                              !item.itemManual ? (
                                <span className="ml-2 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-950">
                                  Produção não finalizada
                                </span>
                              ) : null}
                              {item.itemManual ? (
                                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
                                  Manual
                                </span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2">
                              <div className="w-28">
                                <UndMedidaSelect
                                  unds={unds}
                                  value={item.undMedidaId ?? ""}
                                  disabled={somenteLeitura}
                                  onChange={(id) =>
                                    atualizarUnidade(item.produtoId, id)
                                  }
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-neutral-700">
                              {textoReferencia(item.quantidadePrevista)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-neutral-700">
                              {textoReferencia(item.quantidadeSobraAnterior)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-neutral-700">
                              {textoReferencia(item.quantidadeProduzida)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                value={item.quantidadeTransferencia}
                                inputMode="decimal"
                                readOnly={somenteLeitura}
                                aria-label={`Transferência de ${item.descricao}`}
                                onChange={(event) =>
                                  atualizarItem(
                                    item.produtoId,
                                    event.target.value
                                  )
                                }
                                className={`h-10 w-24 rounded-lg border px-2 text-right text-sm tabular-nums read-only:bg-neutral-100 ${
                                  item.producaoFinalizada === false &&
                                  !item.itemManual
                                    ? "border-amber-400 bg-amber-100 font-semibold"
                                    : "border-neutral-300"
                                }`}
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              {item.itemManual && !somenteLeitura ? (
                                <button
                                  type="button"
                                  onClick={() => removerItem(item.produtoId)}
                                  className="inline-flex items-center gap-1 text-sm font-semibold text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Remover
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <ListasFornecedoresSecao
                listas={form.listas}
                somenteLeitura={somenteLeitura}
                dataFicha={form.dataReferencia}
                previsaoEntrega={
                  DIAS_SEMANA_BASE.find(
                    (dia) => dia.valor === form.previsaoEntregaDiaSemana
                  )?.label ?? ""
                }
                onChange={(listas) => {
                  setForm((atual) => (atual ? { ...atual, listas } : atual));
                  setSujo(true);
                }}
                onImportadas={(quantidade) =>
                  toast.success(
                    quantidade > 1
                      ? `${quantidade} listas adicionadas. Confira e salve a ficha.`
                      : "Lista adicionada. Confira e salve a ficha."
                  )
                }
              />

              <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-neutral-200 pt-4">
                {form.id && !somenteLeitura ? (
                  <button
                    type="button"
                    onClick={() => void atualizarProducao()}
                    disabled={salvando}
                    className="inline-flex items-center gap-1 rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 disabled:opacity-60"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Atualizar produção
                  </button>
                ) : null}
                {form.id ? (
                  <button
                    type="button"
                    onClick={() => void excluirFicha(form.id as number)}
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 px-4 py-2 text-sm font-semibold text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    Excluir
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => void gerarPdfAtual()}
                  className="inline-flex items-center gap-1 rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800"
                >
                  <FileDown className="h-4 w-4" />
                  Gerar PDF
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setTextoConsolidado(
                      textoConsolidadoFichaTransferencia(
                        entradaDoFormulario(form)
                      )
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800"
                >
                  <FileText className="h-4 w-4" />
                  Texto consolidado
                </button>
                {somenteLeitura ? (
                  <button
                    type="button"
                    onClick={() => setSomenteLeitura(false)}
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
                  >
                    <Pencil className="h-4 w-4" />
                    Editar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void salvar()}
                    disabled={salvando}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {salvando ? "Salvando..." : "Salvar"}
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}

      <Modal
        isOpen={ajudaPrevisto}
        onClose={() => setAjudaPrevisto(false)}
        title="De onde vem cada número?"
        description="Só o que foi produzido pode ir na transferência."
      >
        <div className="space-y-3 text-sm leading-relaxed text-neutral-700">
          <p>
            Se a produção da ficha diária ainda não foi finalizada, a
            transferência já vem com a quantidade cheia da sugestão: base menos
            a sobra. Quando alguém marca o item como produzido, a transferência
            passa a usar essa quantidade produzida.
          </p>
          <ul className="space-y-2">
            <li>
              <strong>Base:</strong> o plano da base semanal daquele dia. Só
              consulta.
            </li>
            <li>
              <strong>Sobra dia anterior:</strong> o que sobrou no último dia em
              que a loja abriu, antes desta ficha.
            </li>
            <li>
              <strong>Produzido:</strong> o que a equipe anotou na ficha diária.
              É isso que pode ser transferido.
            </li>
            <li>
              <strong>Transferência:</strong> o que você decide enviar para
              Araçatuba. É o único número que você altera.
            </li>
          </ul>
          <p>
            Se a ficha foi criada antes de terminar a produção, use
            <strong> Atualizar produção</strong>. Os números da ficha diária
            entram de novo. A transferência que você digitou diferente
            permanece.
          </p>
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={() => setAjudaPrevisto(false)}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
            >
              Entendi
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={textoConsolidado != null}
        onClose={() => setTextoConsolidado(null)}
        title="Texto consolidado"
        description="Ficha diária de produção e listas de fornecedores num texto só, pronto para o WhatsApp."
        panelClassName="max-w-2xl"
      >
        <div className="space-y-3">
          <textarea
            readOnly
            value={textoConsolidado ?? ""}
            rows={16}
            onFocus={(event) => event.target.select()}
            className="block w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 font-mono text-sm"
          />
          {sujo ? (
            <p className="text-xs font-medium text-amber-800">
              Há alterações ainda não salvas. O texto já mostra o que está na
              tela.
            </p>
          ) : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setTextoConsolidado(null)}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={() => void copiarTextoConsolidado()}
              className="inline-flex items-center justify-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
            >
              <Copy className="h-4 w-4" />
              Copiar texto
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        title="Adicionar item"
        description="Selecione um produto já cadastrado em Birigui ou Araçatuba."
      >
        <div className="space-y-3">
          <label className="block text-sm font-medium text-neutral-700">
            Produto
            <ReactSelect<ProdutoOpt, false>
              className="mt-1"
              options={produtoOptions}
              value={
                produtoOptions.find(
                  (opt) => opt.value === produtoSelecionado
                ) ?? null
              }
              onChange={(opt: SingleValue<ProdutoOpt>) => {
                setProdutoSelecionado(opt?.value ?? null);
                const produto = produtos.find((item) => item.id === opt?.value);
                setUndMedidaNova(produto?.undMedidaId ?? "");
              }}
              placeholder="Selecionar produto"
              isSearchable
              styles={PRODUTO_SELECT_STYLES}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
            />
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            Unidade de medida
            <div className="mt-1">
              <UndMedidaSelect
                unds={unds}
                value={undMedidaNova}
                onChange={setUndMedidaNova}
              />
            </div>
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            Quantidade para transferência
            <input
              value={quantidadeNova}
              inputMode="decimal"
              onChange={(event) => setQuantidadeNova(event.target.value)}
              className="mt-1 block h-10 w-full rounded-lg border border-neutral-300 px-3 text-sm"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setModalAberto(false)}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmarItemManual}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
            >
              Adicionar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

function CampoLeitura({ label, valor }: { label: string; valor: string }) {
  return (
    <div>
      <p className="text-sm font-medium text-neutral-700">{label}</p>
      <p className="mt-1 text-sm font-semibold text-neutral-900">{valor}</p>
    </div>
  );
}

function BadgeDia({ dia, rotulo }: { dia: number; rotulo: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${TOM_DIA[dia] ?? TOM_DIA[7]}`}
    >
      {rotulo}
    </span>
  );
}

function BadgeListas({ quantidade }: { quantidade?: number }) {
  if (!quantidade) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-900">
      {quantidade} {quantidade === 1 ? "fornecedor" : "fornecedores"}
    </span>
  );
}

function BotaoIcone({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-neutral-200 bg-white text-neutral-700 shadow-sm hover:border-primary hover:text-primary"
    >
      {children}
    </button>
  );
}

function AcoesLista({
  onVer,
  onEditar,
  onPdf,
  onExcluir,
}: {
  onVer: () => void;
  onEditar: () => void;
  onPdf: () => void;
  onExcluir: () => void;
}) {
  return (
    <div className="flex shrink-0 gap-1.5">
      <BotaoIcone label="Visualizar" onClick={onVer}>
        <Eye className="h-4 w-4" />
      </BotaoIcone>
      <BotaoIcone label="Editar" onClick={onEditar}>
        <Pencil className="h-4 w-4" />
      </BotaoIcone>
      <BotaoIcone label="Gerar PDF" onClick={onPdf}>
        <FileDown className="h-4 w-4" />
      </BotaoIcone>
      <BotaoIcone label="Excluir" onClick={onExcluir}>
        <Trash2 className="h-4 w-4" />
      </BotaoIcone>
    </div>
  );
}

export default ControleProducaoFichaTransferenciaPage;
